import React from "react";
import { useRouter } from "@tanstack/react-router";
import { Card, Descriptions, Space, Table, Tabs, Tag, Typography } from "antd";
import type { TabsProps } from "antd";
import dayjs from "dayjs";
import { get } from "@/common/services/httpClient";
import { useAppQuery } from "@/common/hooks/useAppQuery";
import { API_ENDPOINTS, QUERY_KEYS } from "../../api/apiEndpoints";
import { t } from "i18next";
import StatusTag from "@/common/components/Status";
import AmountFormat from "@/common/components/AmountFormat";
import type { ColumnsType } from "antd/es/table";
import type { ApiResponse } from "../../../auth/types/authTypes";
import type { ClaimRequestType, BankDetail, StatusHistoryType, Files } from "../../types/claimType"; // You may move Claim types to a new ClaimTypes file
import { ClaimStatus, ClaimStatusName } from "../../../../common/staticData/enums";
import FormatLocalDate from "../../../../common/components/FormatLocalDate";
import { formatPaddedId } from "../../../../common/helpers/formatHelpers";
import { FilePdfOutlined } from '@ant-design/icons';

// --- Fetch function ---
const fetchClaimDetail = async (id: string): Promise<ClaimRequestType> => {
    const response = await get<ApiResponse<ClaimRequestType>>(
        `${API_ENDPOINTS.CLAIM.DETAIL}?Id=${id}`
    );

    //console.log('response', response);
    return response.data;
};

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const { Title, Text } = Typography;

const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div
        style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: "24px",
            marginBottom: "24px",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
    >
        <h3 style={{ marginBottom: 16 }}>{title}</h3>
        {children}
    </div>
);

// --- Claim Information ---
const ClaimInfo = ({ data }: { data?: ClaimRequestType }) => (
    <Descriptions
        bordered
        size="middle"
        column={{ xs: 1, sm: 2, md: 3 }}
        labelStyle={{
            fontWeight: 600,
            width: 150,
            whiteSpace: "nowrap",
        }}
        contentStyle={{
            wordBreak: "break-word",
        }}
        style={{ marginBottom: 24 }}
    >
        <Descriptions.Item label={t("Claim No")}>
            {formatPaddedId(Number(data?.id)) || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label={t("Policy Holder")}>
            {data?.policyHolderName || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label={t("Patient Name")}>
            {data?.patientName || "N/A"}
        </Descriptions.Item>

        <Descriptions.Item label={t("Card Number")}>
            {data?.cardNumber || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label={t("Claim Date")}>
            {data?.claimDate
                ? dayjs(data?.claimDate).format("DD-MM-YYYY")
                : "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label={t("Status")}>
            <StatusTag status={ClaimStatusName[ClaimStatus[data?.status as keyof typeof ClaimStatus]]} />
        </Descriptions.Item>

        <Descriptions.Item label={t("Claimed Amount")}>
            <AmountFormat
                value={data?.totalAmount ?? 0}
                currencyCode={data?.currencyISOCode ?? ""}
                amountInWord={false}
            />
        </Descriptions.Item>

        <Descriptions.Item label={t("Approved Amount")}>
            <AmountFormat
                value={data?.approvedAmount ?? 0}
                currencyCode={data?.currencyISOCode ?? ""}
                amountInWord={false}
            />
        </Descriptions.Item>

        <Descriptions.Item label={t("Payment")}>
            {data?.paymentType || "N/A"}
        </Descriptions.Item>

        <Descriptions.Item label={t("Reason")}>
            {data?.reason || "N/A"}
        </Descriptions.Item>

    </Descriptions>
);

const historyColumns = [
    { title: t('Created By'), dataIndex: 'userName', key: 'userName' },
    { title: t('Created On'), dataIndex: 'createdOn', key: 'createdOn', render: (value: string) => <FormatLocalDate value={value} /> },
    {
        title: t('Status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
            <StatusTag status={ClaimStatusName[ClaimStatus[status as keyof typeof ClaimStatus]]} />
        ),
    },
];

const HistoryRow = ({ data }: { data?: StatusHistoryType[] }) => (
    <Section title={t('History')}>
        <Table columns={historyColumns} dataSource={data} pagination={false} />
    </Section>
);

// --- Bank Detail ---
const ClaimBankDetail = ({ data }: { data?: BankDetail }) => (
    <Section title={t("Bank Details")}>
        <Descriptions
            bordered
            size="middle"
            column={{ xs: 1, sm: 2, md: 3 }}
            labelStyle={{ fontWeight: 600 }}
        >
            <Descriptions.Item label={t("Bank Name")}>
                {data?.bankName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Account Name")}>
                {data?.accountName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("IBAN")}>
                {data?.iban || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("SWIFT/BIC")}>
                {data?.swift_BIC || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Account No")}>
                {data?.accountNo || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Currency")}>
                {data?.currencyISOCode || "N/A"}
            </Descriptions.Item>
        </Descriptions>
    </Section>
);


// --- Attachments ---
const getFileUrl = (fileName: string) =>
    `${VITE_API_BASE_URL.replace(/api$/, '')}/${fileName}`;

const isPdf = (fileName: string) =>
    fileName?.toLowerCase().endsWith('.pdf');

const ClaimAttachments = ({ files }: { files?: Files[] }) => (
    <Section title={t("Attachments")}>
        {files && files.length > 0 ? (
            <Space wrap>
                {files.map((file, index) => {
                    const url = getFileUrl(file.name);
                    const pdf = isPdf(file.name);

                    return (
                        <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #e8e8e8",
                                padding: 10,
                                borderRadius: 6,
                                width: 60,
                                height: 60,
                            }}
                        >
                            {pdf ? (
                                <FilePdfOutlined style={{ fontSize: 28, color: "#cf1322" }} />
                            ) : (
                                <img
                                    src={url}
                                    alt={file.name}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: 4,
                                    }}
                                />
                            )}
                        </a>
                    );
                })}
            </Space>
        ) : (
            <Text type="secondary">{t("No attachments found")}</Text>
        )}
    </Section>
);

// --- Sub Services Table ---
const serviceColumns: ColumnsType<any> = [
    { title: t("Sub Service"), dataIndex: "serviceName", key: "serviceName" },
    {
        title: t("Amount Claimed"),
        dataIndex: "amount",
        key: "amount",
        render: (value: number, record: any) => (
            <AmountFormat
                value={value ?? 0}
                currencyCode={record?.currencyISOCode}
                amountInWord={false}
            />
        ),
    },
    {
        title: t('Co-Share (%)'),
        dataIndex: 'coShare',
        key: 'coShare',
        width: 150,
        render: (value: number) => value !== null ? <AmountFormat value={value} currencyCode={'%'} /> : t('Not Specified'),
    },
    //{
    //    title: t('Co-Share Amount'),
    //    dataIndex: 'coShareAmount',
    //    key: 'coShare',
    //    width: 150,
    //    render: (value: number, record: any) => value !== null ? <AmountFormat value={value} currencyCode={record?.currencyISOCode} /> : t('Not Specified'),
    //},
    {
        title: t('Covered Amount'),
        dataIndex: 'coveredAmount',
        key: 'coveredAmount',
        render: (value: number, record: any) => {
            if (!record?.isAmountCoverage) {
                return <span>{t("Not Specified")}</span>;
            }

            return (
                <AmountFormat
                    value={value}
                    currencyCode={record?.currencyISOCode}
                />
            );
        }
        // render: (value: number) => <AmountFormat value={value} currencyCode={claim?.currencyISOCode} />,
    },
    {
        title: t('Approved Amount'),
        dataIndex: 'payableAmount',
        key: 'payableAmount',
        render: (value: number, record: any) => (
            <AmountFormat value={value} currencyCode={record?.currencyISOCode} />
        ),
    }

];

//// --- Services Row ---
//const ClaimServicesRow = ({ data }: { data?: any[] }) => (
//    <Section title={t("Claimed Services")}>
//        <Table
//            columns={serviceColumns}
//            dataSource={data}
//            rowKey="id"
//            pagination={{ pageSize: 10 }}
//            onRow={(record: any) => {
//                return {
//                    style: record.isServiceCovered
//                        ? { backgroundColor: '#e6ffe6' } // light green
//                        : {},
//                };
//            }}
//        />
//    </Section>
//);

// --- Main Component ---
const Detail: React.FC = () => {

    const { state } = useRouter().state.location;
    const record = (state as { record?: ClaimRequestType })?.record;
    const Id = record?.id?.toString() || "";

    const { data: claimData, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.CLAIM.DETAIL, Id],
        () => fetchClaimDetail(Id),
        { enabled: !!Id }
    );

    const categoryColumns = React.useMemo(() => [

        { title: t("Main Service"), dataIndex: "serviceCategoryName" },

    ], [claimData]);

    const servicesByCategory = React.useMemo(() => {
        if (!claimData?.services?.length) return [];

        const map = new Map();

        claimData.services.forEach((s) => {
            if (!map.has(s.serviceCategoryId)) {
                map.set(s.serviceCategoryId, {
                    serviceCategoryId: s.serviceCategoryId,
                    serviceCategoryName: s.serviceCategoryName,
                    //coveredAmount: s.coveredAmount,
                    //balance: s.balance,
                    services: [],
                });
            }

            map.get(s.serviceCategoryId).services.push({
                ...s,
                currencyISOCode: claimData.currencyISOCode
            });
        });

        return Array.from(map.values());
    }, [claimData]);

    const renderServiceItems = (category: any) => {
        return (
            <Table
                dataSource={category.services}
                //rowKey="serviceCategoryItemId"
                rowKey={(record) => record.id || record.serviceCategoryItemId}
                pagination={false}
                size="small"
                columns={serviceColumns}
                onRow={(record) => ({
                    style: {
                        backgroundColor: record.isServiceCovered
                            ? '#e6ffe6' // light green for covered
                            : '#ffe6e6', // light red for not covered
                    },
                })}
                expandable={{
                    rowExpandable: (record) =>
                        !!record.customerExceptions?.length,

                    expandedRowRender: (record) => {
                        if (!record.customerExceptions?.length) return null;

                        return (
                            <div style={{ padding: 12, background: "#fafafa" }}>
                                {record.customerExceptions.map((ex: any) => (
                                    <Tag key={ex.name} color="orange">
                                        {ex.name}
                                    </Tag>
                                ))}
                            </div>
                        );
                    },
                }}
            />
        );
    };

    const onChange = (key: string) => console.log("Selected Tab:", key);

    //const servicesWithCurrency = claimData?.services?.map(s => ({
    //    ...s,
    //    currencyISOCode: claimData?.currencyISOCode
    //}));

    const items: TabsProps["items"] = [
        {
            key: "1",
            label: t("Services"),
            //children: <ClaimServicesRow data={servicesWithCurrency} />,
            children: <Section title={t("Claimed Services")}>
                <Table
                    dataSource={servicesByCategory}
                    columns={categoryColumns}
                    rowKey="serviceCategoryId"
                    pagination={false}
                    expandable={{
                        expandedRowRender: renderServiceItems,
                        defaultExpandAllRows: true,
                    }}
                />
            </Section>,
        },
        {
            key: "2",
            label: t("Attachments"),
            children: <ClaimAttachments files={claimData?.files} />,
        },
        {
            key: '3',
            label: t('History'),
            children: <HistoryRow data={claimData?.statusHistory} />,
        },
    ];

    return (
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
            <Card style={{ marginBottom: 24 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    {/* --- Left Section: Title + Status --- */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        {/* --- Left Section: Title + Status + Provider --- */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Title level={3} style={{ margin: 0 }}>
                                    {t("Claim Detail")}
                                </Title>
                                <StatusTag
                                    status={
                                        ClaimStatusName[
                                        ClaimStatus[claimData?.status as keyof typeof ClaimStatus]
                                        ]
                                    }
                                />
                            </div>

                            {/* --- New Line: Medical Provider --- */}
                            <Title level={4} style={{ margin: 0, color: "#555" }}>
                                <Text>{claimData?.medicalProviderName}</Text>
                            </Title>
                            {claimData?.isBabyBorn && (
                                <Title level={4} style={{ margin: 0, color: "#8B0000" }}>
                                    <Text style={{ color: "#8B0000", fontWeight: 600 }}>New Baby Born</Text>
                                </Title>
                            )}

                        </div>

                    </div>
                    {/* --- Right Section: Balance --- */}
                    <div>
                        <Title level={3} style={{ margin: 0 }}>{t("Balance")}</Title>
                        <Text strong style={{ fontSize: 16 }}>
                            <AmountFormat
                                value={claimData?.balance ?? 0}
                                currencyCode={claimData?.currencyISOCode}
                                currencyName={claimData?.currencyISOCode}
                                amountInWord={false}
                            />
                        </Text>
                    </div>

                </div>
            </Card>


            {isLoading && <p>{t("Loading...")}</p>}
            {isError && <p>{t("Error fetching data")}</p>}

            {!isLoading && !isError && claimData && (
                <>
                    <ClaimInfo data={claimData} />

                    {
                        claimData.paymentType === "Bank" && (
                            <ClaimBankDetail data={claimData} />
                        )}

                    <Tabs
                        defaultActiveKey="1"
                        items={items}
                        onChange={onChange}
                        destroyInactiveTabPane={false}
                    />
                </>
            )}
        </div>
    );
};

export default Detail;


